import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Link, Navigate, useParams } from 'react-router-dom';
import { resolveReceiptCode } from '../../api/receipt.api';
import {
  BreakdownRow,
  DetailField,
  SectionCard
} from '../../components/detail/detail-parts';
import { StoreBreakdownRows } from '../../components/detail/StoreBreakdownRows';
import { DonationBreakdownLines } from '../../components/donation/DonationBreakdownLines';
import { Button } from '../../components/ui/button';
import { LOGO_PATH } from '../../constants/logo';
import type {
  PublicReceipt,
  PublicReceiptItem
} from '../../types/public-receipt.types';
import type { PaymentMethodDisplay } from '../../types/payment-method-display';
import { formatCurrency } from '../../utils/fiat-currency.util';
import { hasLiveSession, loginPath } from '../../utils/login-return';
import { TransactionPaymentMethod } from '../transactions/components/TransactionPaymentMethod';

/*
 * The page a printed receipt's link opens, signed in or not. A visitor who owns the receipt goes
 * to its transaction; anyone else sees the store's receipt, read-only, for 30 days after
 * payment; after that, or for a code that doesn't check out, the visitor is asked to sign in.
 */

const PUBLIC_RECEIPT_QUERY_KEY = 'publicReceipt';

const RECENT_ACTIVITY_PATH = '/';

/** The note under a donation the slip holder sees: it was charged with the order. */
const DONATION_PAID_NOTE = 'Paid';

/** The logo bar: the page is reached from outside the portal, so there is nothing to go back to. */
function ReceiptHeader() {
  return (
    <div className="border-b border-gray-100 bg-white">
      <div className="mx-auto flex max-w-2xl items-center px-4 py-4">
        <img src={LOGO_PATH} alt="AyeFace" className="h-8 w-auto" />
      </div>
    </div>
  );
}

function CenteredMessage({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
      {children}
    </div>
  );
}

const CTA_BUTTON =
  'mt-6 h-12 w-full rounded-xl bg-orange-500 text-sm font-semibold text-white hover:bg-orange-600';

/** A code the public view won't show: sign in, or for a signed-in visitor, their own activity. */
function SignInRequired({
  code,
  signedIn
}: {
  code: string;
  signedIn: boolean;
}) {
  if (signedIn) {
    return (
      <CenteredMessage title="This receipt isn't available">
        <p className="mt-2 text-sm text-gray-600">
          Your own receipts are in Recent activity.
        </p>
        <Button asChild className={CTA_BUTTON}>
          <Link to={RECENT_ACTIVITY_PATH}>Go to Recent activity</Link>
        </Button>
      </CenteredMessage>
    );
  }
  return (
    <CenteredMessage title="Sign in to see your receipts">
      <p className="mt-2 text-sm text-gray-600">
        Your receipts are in your AyeFace account.
      </p>
      <Button asChild className={CTA_BUTTON}>
        <Link to={loginPath(`/e/${code}`)}>Sign in</Link>
      </Button>
    </CenteredMessage>
  );
}

function ItemRow({
  item,
  currency
}: {
  item: PublicReceiptItem;
  currency: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-sm text-gray-700">
      <div className="min-w-0">
        <p className="text-gray-900">
          {item.quantity} × {item.name}
        </p>
        {item.variant && (
          <p className="text-xs text-gray-500">{item.variant}</p>
        )}
      </div>
      <span className="shrink-0">{formatCurrency(item.amount, currency)}</span>
    </div>
  );
}

const methodDisplay = (
  method: NonNullable<PublicReceipt['payment_method']>
): PaymentMethodDisplay => ({
  value: method.icon,
  label: method.label,
  detail: method.last4 ? `•••• ${method.last4}` : null,
  icon: method.icon,
  group: method.icon
});

/** A signed-in visitor who is not this receipt's customer: say so, and point at their own. */
function AnotherAccountNote() {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700">
      <p>This receipt is on another AyeFace account.</p>
      <Link
        to={RECENT_ACTIVITY_PATH}
        className="mt-1 inline-block font-semibold text-orange-600 hover:underline"
      >
        Go to your Recent activity
      </Link>
    </div>
  );
}

export function PublicReceiptView({
  receipt,
  code,
  signedIn
}: {
  receipt: PublicReceipt;
  /** The printed code, for signing in and coming back to it. */
  code: string;
  /** The visitor is signed in, so the backend found them not to be this receipt's customer. */
  signedIn: boolean;
}) {
  const { currency, donation } = receipt;
  const date = dayjs(receipt.date).format('MMM DD, YYYY, hh:mm A');
  // What the bank statement shows: the store's charge and the donation's, together.
  const totalPaid = receipt.total + (donation?.amount ?? 0);

  return (
    <>
      <div className="mx-auto mt-4 flex max-w-2xl flex-col items-center px-4 pb-4 pt-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">
          Total paid
        </p>
        <p className="mt-2 text-3xl font-semibold text-orange-500">
          {formatCurrency(totalPaid, currency)}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          {receipt.store_name} · {date}
        </p>
        <p className="mt-1 text-xs text-gray-500">{receipt.merchant_name}</p>
      </div>

      <div className="mx-auto max-w-2xl space-y-3 px-4 py-4 pb-10">
        {signedIn && <AnotherAccountNote />}
        <SectionCard title="Payment Breakdown">
          <StoreBreakdownRows
            breakdown={receipt}
            currency={currency}
            storeName={receipt.store_name}
            adjustment={receipt.adjustment}
          >
            {receipt.items.length > 0 ? (
              <div className="mb-2 border-b border-gray-100 pb-2">
                {receipt.items.map((item, index) => (
                  <ItemRow
                    key={`${item.name}-${index}`}
                    item={item}
                    currency={currency}
                  />
                ))}
              </div>
            ) : (
              <BreakdownRow
                label={`Purchase at ${receipt.store_name}`}
                value=""
              />
            )}
          </StoreBreakdownRows>
          {donation && (
            <DonationBreakdownLines
              charityName={donation.charity_name}
              amount={donation.amount}
              currency={donation.currency}
              note={DONATION_PAID_NOTE}
              totalLabel="Total paid"
              total={totalPaid}
            />
          )}
        </SectionCard>

        <SectionCard title="Order details" aside={receipt.store_name}>
          <DetailField
            label="Order ID"
            value={receipt.order_id}
            copyValue={receipt.order_id}
          />
          <DetailField
            label="Transaction ID"
            value={receipt.transaction_id}
            copyValue={receipt.transaction_id}
          />
          {receipt.payment_method && (
            <TransactionPaymentMethod
              paymentMethod={null}
              display={methodDisplay(receipt.payment_method)}
            />
          )}
        </SectionCard>

        {!signedIn && (
          <div className="pt-3 text-center">
            <p className="text-sm text-gray-600">
              Is this your receipt? Sign in to download it and see your rewards.
            </p>
            <Button
              asChild
              variant="outline"
              className="mt-3 h-12 w-full rounded-xl border-orange-500 text-sm font-semibold text-orange-600 hover:bg-orange-50 hover:text-orange-600"
            >
              <Link to={loginPath(`/e/${code}`)}>Sign in</Link>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

const PublicReceiptPage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const signedIn = hasLiveSession();
  const resolution = useQuery({
    // The answer depends on who is asking, and a visit after signing in must not show the
    // answer from before it, so the session is part of the key and nothing is kept past the page.
    queryKey: [PUBLIC_RECEIPT_QUERY_KEY, code, signedIn],
    queryFn: () => resolveReceiptCode(code),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false
  });

  const body = () => {
    if (resolution.isLoading) {
      return (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
        </div>
      );
    }
    if (resolution.isError || !resolution.data) {
      return (
        <CenteredMessage title="We couldn't open this receipt">
          <p className="mt-2 text-sm text-gray-600">
            Please check your connection and try again.
          </p>
          <Button className={CTA_BUTTON} onClick={() => resolution.refetch()}>
            Try again
          </Button>
        </CenteredMessage>
      );
    }
    const outcome = resolution.data;
    switch (outcome.result) {
      case 'owner':
        return (
          <Navigate
            to={`/transactions/${encodeURIComponent(outcome.transaction_id)}`}
            replace
          />
        );
      case 'receipt':
        return (
          <PublicReceiptView
            receipt={outcome.receipt}
            code={code}
            signedIn={signedIn}
          />
        );
      default:
        return <SignInRequired code={code} signedIn={signedIn} />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <ReceiptHeader />
      {body()}
    </div>
  );
};

export default PublicReceiptPage;
